import { useState, useEffect } from "react";
import Constant from "../../utils/Constant";

const Categories = ({ onCategorySelect, handleAllItemsClick,searchQuery, newProducts, reset }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCategory, setOpenCategory] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [hasSelectedCategory, setHasSelectedCategory] = useState(false);
  const [isNewProductActive, setIsNewProductActive] = useState(false);

  // NEW: Effect to handle reset from parent component
  useEffect(() => {
    if (reset) {
      setActiveCategory(null);
      setOpenCategory({});
      setHasSelectedCategory(false);
      setIsNewProductActive(false);
    }
  }, [reset]);

  // MOVED: Effect to handle search query changes - moved to main component level
  useEffect(() => {
    setIsNewProductActive(false);
  }, [searchQuery]);
 
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${Constant.BASE_URL}/api/v1/getCategories`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setCategories(data);
        setIsLoading(false);
      } catch (error) {
        setError(error.message);
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelect = (categoryData, levelType) => {
    onCategorySelect(categoryData, levelType);
    if (isMobileView && levelType === "subchild") {
      setSidebarOpen(false);
    }
  };

  const handleNewProductClick = () => {
    setIsNewProductActive(true);
    setActiveCategory(null);
    setOpenCategory({});
    setHasSelectedCategory(true);
    newProducts();
  };

  const CategoryItem = ({
    category,
    level = 0,
    parentCategory = null,
  }) => {
    const categoryId =
      category.main_category_id ||
      category.child_category_id ||
      category.sub_child_category_id;

    const isOpen = openCategory[level] === categoryId;
    const paddingLeft = isMobileView ? "1rem" : `${level * 1.5}rem`;

    const isActive = () => {
      if (!activeCategory) return false;
      
      if (level === 0) {
        return (
          activeCategory.main_category_id === categoryId &&
          !activeCategory.child_category_id &&
          !activeCategory.sub_child_category_id
        );
      } else if (level === 1) {
        return (
          activeCategory.main_category_id === (parentCategory?.main_category_id || category.main_category_id) &&
          activeCategory.child_category_id === categoryId &&
          !activeCategory.sub_child_category_id
        );
      } else if (level === 2) {
        return (
          activeCategory.main_category_id === parentCategory?.main_category_id &&
          activeCategory.child_category_id === parentCategory?.child_category_id &&
          activeCategory.sub_child_category_id === categoryId
        );
      }
      return false;
    };

    const handleClick = () => {
      const categoryData = {
        main_category_id:
          parentCategory?.main_category_id || category.main_category_id,
        child_category_id:
          parentCategory?.child_category_id || category.child_category_id,
        sub_child_category_id: category.sub_child_category_id,
      };

      let levelType = "main";
      if (parentCategory?.main_category_id && category.child_category_id) {
        levelType = "child";
      }
      if (parentCategory?.child_category_id && category.sub_child_category_id) {
        levelType = "subchild";
      }

      // ALWAYS reset New Product state when any category is clicked
      setIsNewProductActive(false);
      setHasSelectedCategory(true);
      
      const currentlyActive = isActive();
      setActiveCategory(currentlyActive ? null : categoryData);
      
      setOpenCategory((prev) => ({
        ...prev,
        [level]: isOpen ? null : categoryId,
      }));
      
      handleSelect(categoryData, levelType);
    };
    
    // REMOVED: The useEffect that was causing the issue has been moved to main component level

    const active = isActive();

    return (
      <div className="p-1">
        <button
          onClick={handleClick}
          className={`w-full flex items-center p-1 2xl:p-6 transition-all duration-300 text-left justify-start rounded-md ${
            active
              ? "bg-blue-100 text-[#099CD6] font-medium"
              : "hover:bg-gray-50 hover:text-[#099CD6]"
          }`}
          style={{ paddingLeft }}
        >
          <span className="transition-colors duration-300 xsm:text-[14px] flex-1">
            {category.main_category_name ||
              category.child_category_name ||
              category.sub_child_category_name}
          </span>

          {(category.children?.length > 0 ||
            category.subchildren?.length > 0) && (
            <svg
              className={`w-[18.67px] h-[18.83px] 2xl:w-8 2xl:h-8 text-gray-400 transform transition-transform duration-300 ml-2 flex-shrink-0 ${
                isOpen ? "rotate-90" : "rotate-0"
              }`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {isOpen && (
          <div className="transform origin-top transition-all duration-300 xsm:text-[11px] mt-1">
            {category.children?.map((child, index) => (
              <CategoryItem
                key={`child-${categoryId}-${index}`}
                category={child}
                level={level + 1}
                parentCategory={{
                  main_category_id: category.main_category_id,
                  main_category_name: category.main_category_name,
                }}
              />
            ))}

            {category.subchildren?.map((subchild, index) => (
              <CategoryItem
                key={`subchild-${categoryId}-${index}`}
                category={subchild}
                level={level + 2}
                parentCategory={{
                  main_category_id: category.main_category_id,
                  child_category_id: category.child_category_id,
                  child_category_name: category.child_category_name,
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading)
    return <div className="text-center p-4 text-gray-500">Loading categories...</div>;

  if (error)
    return (
      <div className="text-center p-4 text-red-500">
        Error loading categories: {error}
      </div>
    );

  return (
    <>
      <button
        className="md:hidden w-full mb-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between transform transition-all duration-300 hover:shadow-md"
        onClick={() => setSidebarOpen(!isSidebarOpen)}
      >
        <span className="text-[#099CD6] font-Poppins 2xl:text-2xl">
          Categories
        </span>
        <svg
          className={`w-6 h-6 transform transition-transform duration-300 ${
            isSidebarOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        className={`w-full md:w-64 2xl:w-96 flex-shrink-0 transition-all duration-300 ease-in-out ${
          isMobileView && !isSidebarOpen ? "hidden" : "block"
        }`}
      >
        <div className="border sticky top-5 bottom-0 z-20 rounded-lg shadow-lg transition-shadow hover:shadow-xl bg-white w-full md:max-w-[300px] flex flex-col">
          {/* Fixed Header */}
          <div className="bg-gray-50 p-4 2xl:p-6 border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-22px font-Poppins font-normal text-[#099CD6] 2xl:text-4xl">
                Categories
              </h2>
            </div>
          </div>
          
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-200px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="ps-3 pe-2 font-normal font-Poppins text-left">
              {/* New Product Button */}
              <div
                className={`py-2 text-sm font-semibold cursor-pointer transition-all duration-300 rounded-md p-2 m-1 ${
                  isNewProductActive
                    ? "bg-blue-100 text-[#099CD6] font-medium"
                    : "text-gray-800 hover:bg-gray-50 hover:text-[#099CD6]"
                }`}
                onClick={handleNewProductClick}
              >
                New Product
              </div>

              {/* Categories */}
              {categories.map((category, index) => (
                <CategoryItem
                  key={`main-${index}`}
                  category={category}
                  isMobileView={isMobileView}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Categories;